import { ArrowUpRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useNews } from "@/hooks/useNews";
import { Skeleton } from "@/components/ui/skeleton";

export const CuratedNews = () => {
  const { news, loading, error } = useNews();
  const navigate = useNavigate();

  const items = news.slice(0, 6);

  if (error) {
    return (
      <section className="bg-[#050505] text-center py-20">
        <p className="text-red-500">Could not fetch news.</p>
      </section>
    );
  }

  return (
    <section
      id="news"
      data-testid="curated-news-section"
      className="relative bg-[#050505] px-6 md:px-10 lg:px-16 py-20 md:py-28"
    >
      <div className="flex items-end justify-between mb-10 md:mb-14 border-b border-white/10 pb-6">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-flare mb-3">
            /01 — curated news
          </p>
          <h2
            data-testid="news-heading"
            className="font-display font-extrabold text-4xl md:text-6xl uppercase tracking-tighter leading-[0.95]"
          >
            What's <span className="italic font-normal">moving</span>
            <br />
            this week.
          </h2>
        </div>
        <Link
          to="/news"
          data-testid="news-see-all"
          className="hidden md:flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-bone hover:text-flare transition-colors pb-2"
        >
          See all news <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-white/10 reveal-stagger">
        {loading
          ? Array.from({ length: 6 }).map((_, idx) => (
              <div
                key={idx}
                className="bg-[#050505] border-r border-b border-white/10 p-6 md:p-8 min-h-[220px] flex flex-col justify-between"
              >
                <div className="flex items-start justify-between gap-4">
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-4 w-16" />
                </div>
                <Skeleton className="h-8 w-full mt-8" />
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/5">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-4" />
                </div>
              </div>
            ))
          : items.map((item, idx) => (
              <button
                key={item.slug}
                onClick={() => navigate(`/news/${item.slug}`)}
                data-testid={`news-card-${item.slug}`}
                className="group relative text-left bg-[#050505] border-r border-b border-white/10 p-6 md:p-8 hover:bg-[#0C0C0C] transition-colors cursor-pointer min-h-[220px] flex flex-col justify-between"
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-flare border border-flare/50 px-2 py-1">
                    {item.tag}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone-muted">
                    {item.time}
                  </span>
                </div>
                <h3 className="font-display font-bold text-xl md:text-2xl leading-tight tracking-tight mt-8 text-bone group-hover:text-flare transition-colors">
                  {item.title}
                </h3>
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone-muted">
                    by {item.author}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-bone-muted group-hover:text-flare group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                </div>
                {idx === 0 && (
                  <span className="absolute top-0 left-0 bg-flare text-black font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-0.5">
                    Hot
                  </span>
                )}
              </button>
            ))}
      </div>

      {/* Mobile-only "See all" — desktop has it in the header */}
      <Link
        to="/news"
        data-testid="news-see-all-mobile"
        className="md:hidden mt-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] border border-white/15 px-5 py-3 hover:border-flare hover:text-flare transition-colors"
      >
        See all news <ArrowUpRight className="w-4 h-4" />
      </Link>
    </section>
  );
};
